import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QueryParamsContext } from '~/providers/query-params';
import {
  CreateAccount,
  CreateAccountProps,
} from '~/screens/create-account/create-account.component';
import { createUser, fetchFunnelPacket } from '~/services';
import { CreateUserParams } from '~/services/create-user';
import { Utility } from '~/typings/context';
import { FunnelTrack, OriginationSource } from '~/typings/graphql';
import { INTERNAL_ROUTES, SPONSORED_SOLAR_ROUTES } from '~/utils/constants';
import { getSubRoute } from '~/utils/redirects';
import { store } from '~/utils/store';
import { getUserIpFromStore, sendToSegment, updateTrackingIdentity } from '~/utils/tracking';

const trackStarted = utility => {
  sendToSegment({
    type: 'user_step_started',
    utilityId: utility.id,
    funnelTrack: 'sponsored-solar',
  });
};

const trackOptInToSms = (isUserOptinToSMS, userId) => {
  if (isUserOptinToSMS) {
    sendToSegment({
      type: 'track_sms_opt_in_preference',
      value: isUserOptinToSMS,
      userId,
    });
  }
};

const trackCompleted = async context => {
  const { funnelPacket, queryParams, zipCode, utility } = context;
  /* We are fetching the IP again as we don't want to include
   * the user's IP in the funnel packet.
   */
  const userIp = getUserIpFromStore();
  sendToSegment({
    type: 'track_query_params',
    funnelPacket: funnelPacket!,
    userIp,
    queryParams: queryParams!,
  });
  sendToSegment({
    type: 'user_step_completed',
    userIp,
    utilityName: utility.displayName,
    zipCode,
    state: utility.state.name,
    funnelPacket: funnelPacket!,
  });
};

export interface CreateAccountWrapperProps {
  onUserCreation: () => void;
  originationSource: OriginationSource;
}
export const CreateAccountWrapper = ({
  onUserCreation,
  originationSource,
}: CreateAccountWrapperProps) => {
  const {
    promoCode,
    unparsedQueryParams,
    utmParams,
    inviteCode,
    firstName,
    lastName,
    email,
    phone,
  } = useContext(QueryParamsContext);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const utilityFromStore = store.get('UTILITY_SELECTION')!;
  const userId = store.get('USER_ID');
  const zipCode = store.get('ZIP_CODE');
  const utility: Utility = {
    ...utilityFromStore,
    funnelTrack: utilityFromStore?.funnelTrack as FunnelTrack,
  };

  useEffect(() => {
    /**
     * If, utility and zipcode are not available,
     * then we couldn't proceed. So we just redirect
     * user to landing page.
     */
    if (zipCode === undefined || utility.id === undefined) {
      /**
       * TODO This should redirect to sponsored solar landing page.
       * just using this page for time being
       */
      navigate(INTERNAL_ROUTES.EXPIRED_SESSION, {
        state: { url: INTERNAL_ROUTES.SPONSORED_SOLAR },
      });
    } else if (userId) {
      /**
       * Prevent user from going back to create user page
       * if user is already created.
       */
      navigate(
        getSubRoute(INTERNAL_ROUTES.SPONSORED_SOLAR, SPONSORED_SOLAR_ROUTES.UTILITY_CONNECT),
      );
    } else {
      trackStarted(utility);
    }
  }, []);

  const { funnelTrack } = utility;

  const createNewUser = async context => {
    const userIp = getUserIpFromStore();
    const { userId } = await createUser({
      promoCode,
      businessName: context.businessName,
      email: context.email,
      firstName: context.firstName,
      funnelId: funnelTrack === FunnelTrack.Waitlist ? FunnelTrack.Waitlist : context.funnelId,
      inviteCode: context.inviteCode,
      ipAddress: userIp,
      generalContractIds: context.generalContractIds,
      lastName: context.lastName,
      originationSource,
      password: context.password,
      smsOptIn: context.optInToSMS,
      phone: context.phone,
      planTemplateId: 609, // placeholder plan template
      unparsedQueryParams: context.unparsedQueryParams,
      utilityId: utility.id,
      utmParams: context.utmParams,
      zipCode,
    } as CreateUserParams);

    trackOptInToSms(context.optInToSMS, userId);

    const funnelPacket = await fetchFunnelPacket({ userId });
    store.set('ACCOUNT_ID', funnelPacket.account.id);
    updateTrackingIdentity(funnelPacket);
    return funnelPacket;
  };

  const handleUserSubmission = useCallback(
    async data => {
      setError('');
      setPending(true);
      try {
        const funnelPacket = await createNewUser(data);
        const agentName: string | undefined = utmParams?.utm_agent?.toString();

        const updatedQueryParams = {
          ...unparsedQueryParams,
          ...utmParams,
        };

        if (agentName) {
          updatedQueryParams.utm_agent = agentName;
        }
        setPending(false);
        onUserCreation();
        trackCompleted({ funnelPacket, queryParams: updatedQueryParams, zipCode, utility });
      } catch (e) {
        setError('Something went wrong, please try again.');
      }
    },
    [pending, error, utility, zipCode, utmParams, unparsedQueryParams, promoCode],
  );

  const props: CreateAccountProps = {
    context: {
      agentName: '',
      businessName: '',
      email: email || '',
      firstName: firstName || '',
      googleUserError: '',
      inviteCode: inviteCode || '',
      lastName: lastName || '',
      phone: phone || '',
      optInToSMS: false,
      promoCode: promoCode || '',
      utility,
      zipCode: zipCode!,
      funnelId: '',
      forceWaitlist: false,
    },
    error,
    flags: {
      disableEmail: false,
      hideGoogleButton: true,
      showFaqButton: true,
      requireBusinessName: false,
      requirePhone: false,
      hidePassword: false,
      hideTerms: false,
      showPhone: true,
      showZipCode: true,
      isInviteCodeRequired: false,
      hideLearnMoreDialogSelector: true,
      hideBackLink: true,
      showInviteCode: true,
      showStep: true,
    },
    next: handleUserSubmission,
    pending,
    overrideCopy: _context => {
      return {
        title: 'Create your account',
        description: 'We need some information about you to create your Arcadia account.',
      };
    },
  };
  return zipCode !== undefined && utility.id !== undefined ? <CreateAccount {...props} /> : null;
};
